
/**
 * Downloads a resource with progress tracking. This function reads the response body
 * in chunks, allowing progress tracking based on the Content-Length header, if available.
 * Based on https://javascript.info/fetch-progress
 *
 * @param {Response} response - The Fetch API Response object to read the data from.
 * @param {Function} progressCallback - Callback function that is called with the download progress.
 * The progress is a number between 0 and 1 representing the portion of the content that has been downloaded.
 * @returns {Promise<string>} A promise that resolves to the downloaded content as a string.
 * @throws {Error} Throws an error if the response body is not available or if an error occurs during reading.
 *
 * @example
 * // Example usage
 * fetch('https://example.com/data')
 *   .then(response => downloadWithProgress(response, progress => {
 *       console.log(`Downloaded ${progress * 100}%`);
 *   }))
 *   .then(data => {
 *       console.log('Downloaded Data:', data);
 *   })
 *   .catch(error => {
 *       console.error('Download error:', error);
 *   });
 */
export async function downloadWithProgress(response, progressCallback) {
    if (!response.body) {
        throw new Error('Invalid response body');
    }

    const reader = response.body.getReader();

    // Check if Content-Length is available
    const contentLength = +response.headers.get('Content-Length') || 0;
    let receivedLength = 0;
    let chunks = [];
    try {
        while (true) {
            const {done, value} = await reader.read();
            if (done) break;

            chunks.push(value);
            receivedLength += value.length;

            if (contentLength) {
                let portionComplete = receivedLength / contentLength;
                progressCallback(portionComplete);
            }
        }
    } catch (error) {
        console.error('Error reading the response:', error);
        throw error; // Rethrow the error for further handling
    }

    // Concatenate chunks into single Uint8Array
    let chunksAll = new Uint8Array(receivedLength);
    let position = 0;
    for (let chunk of chunks) {
        chunksAll.set(chunk, position);
        position += chunk.length;
    }

    // Decode into a string and return
    return new TextDecoder("utf-8").decode(chunksAll);
}
